import { useSearchParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '@/lib/api';
import { formatCents } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import {
  ESTIMATE_STATUS_BADGE,
  PO_STATUS_BADGE,
  RETURN_STATUS_BADGE,
  COUNTERTOP_JOB_STATUS_BADGE,
} from '@/lib/badges';
import { Loader2 } from 'lucide-react';
import type { ComponentProps, ReactNode } from 'react';

/** A transaction search hit (estimate, PO, return, countertop job). */
interface TransactionRow {
  id: number;
  number: string;
  party: string;
  status: string;
  /** Integer cents; absent for countertop jobs. */
  total?: number;
}

interface SearchResult {
  customers: { id: number; name: string; code: string | null }[];
  customers_total: number;
  suppliers: { id: number; name: string; code: string | null }[];
  suppliers_total: number;
  items: { id: number; name: string; code: string | null; category: string }[];
  items_total: number;
  estimates: TransactionRow[];
  estimates_total: number;
  purchase_orders: TransactionRow[];
  purchase_orders_total: number;
  sales_returns: TransactionRow[];
  sales_returns_total: number;
  purchase_returns: TransactionRow[];
  purchase_returns_total: number;
  countertop_jobs: TransactionRow[];
  countertop_jobs_total: number;
}

interface SearchResponse {
  data: SearchResult;
}

// Escapes regex metacharacters so a raw query token can be dropped into a
// RegExp without being interpreted as a pattern (e.g. a customer named
// "3M (416)" shouldn't blow up the search page).
function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

// Splits `text` on case-insensitive occurrences of any of `tokens` and wraps
// the matches in <mark> so the matched substrings stand out in result rows.
// String.split with a single capturing group alternates plain/matched text —
// odd indices are always the captured matches — which sidesteps the
// stateful-lastIndex pitfall of re-using a global RegExp with .test()/.exec().
function highlight(text: string, tokens: string[]): ReactNode {
  const cleaned = tokens.filter((t) => t.length > 0);
  if (cleaned.length === 0) return text;

  const pattern = new RegExp(`(${cleaned.map(escapeRegExp).join('|')})`, 'gi');
  const parts = text.split(pattern);

  return parts.map((part, i) =>
    i % 2 === 1 ? (
      <mark key={i} className="bg-amber-200/70 text-inherit rounded-sm px-0.5">
        {part}
      </mark>
    ) : (
      <span key={i}>{part}</span>
    ),
  );
}

function CategoryBadge({ category }: { category: string }) {
  const isGoods = category === 'goods';
  return (
    <span
      className={[
        'inline-flex items-center rounded px-1.5 py-0.5 text-xs font-medium',
        isGoods
          ? 'bg-blue-50 text-blue-700 ring-1 ring-blue-600/20'
          : 'bg-purple-50 text-purple-700 ring-1 ring-purple-600/20',
      ].join(' ')}
    >
      {category}
    </span>
  );
}

/** Shape of the entries in the shared status-badge maps (`@/lib/badges`). */
type BadgeInfo = { variant: ComponentProps<typeof Badge>['variant']; label: string };

/**
 * Renders transaction result rows (estimate / PO / return / countertop job).
 * `number` and `party` are highlighted; a status badge (from `badgeMap`) and,
 * when present, the amount sit on the right. `total` is absent for countertop
 * jobs, so the amount is only rendered when it is a number.
 */
function TransactionRows({
  rows,
  hrefFor,
  badgeMap,
  tokens,
}: {
  rows: TransactionRow[];
  hrefFor: (id: number) => string;
  badgeMap: Record<string, BadgeInfo>;
  tokens: string[];
}) {
  return (
    <>
      {rows.map((row) => {
        const badge = badgeMap[row.status];
        return (
          <Link
            key={row.id}
            to={hrefFor(row.id)}
            className="flex items-center justify-between gap-3 px-4 py-2.5 hover:bg-muted/50 transition-colors"
          >
            <span className="flex min-w-0 items-baseline gap-2">
              <span className="text-sm font-medium whitespace-nowrap">
                {highlight(row.number, tokens)}
              </span>
              <span className="truncate text-xs text-muted-foreground">
                {highlight(row.party, tokens)}
              </span>
            </span>
            <span className="flex shrink-0 items-center gap-2">
              <Badge variant={badge?.variant ?? 'gray'}>{badge?.label ?? row.status}</Badge>
              {row.total != null && (
                <span className="text-sm tabular-nums">{formatCents(row.total)}</span>
              )}
            </span>
          </Link>
        );
      })}
    </>
  );
}

interface SectionProps {
  title: string;
  /** Number of rows actually rendered (capped by the server's SEARCH_LIMIT). */
  shown: number;
  /** True COUNT(*) of matching rows — may exceed `shown`. */
  total: number;
  emptyLabel: string;
  children: React.ReactNode;
}

function Section({ title, shown, total, emptyLabel, children }: SectionProps) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-2">
        <h2 className="text-sm font-semibold text-foreground">
          {title} <span className="font-normal text-muted-foreground">({total})</span>
        </h2>
      </div>
      {total > shown && (
        <p className="text-xs text-muted-foreground -mt-1 mb-2">
          Showing {shown} of {total} — refine your search to narrow results.
        </p>
      )}
      {total === 0 ? (
        <p className="text-sm italic text-muted-foreground py-2">{emptyLabel}</p>
      ) : (
        <div className="rounded-md border divide-y">{children}</div>
      )}
    </div>
  );
}

export default function Search() {
  const [params] = useSearchParams();
  const q = params.get('q') ?? '';

  const { data, isLoading } = useQuery({
    queryKey: ['search', q],
    queryFn: () => apiFetch<SearchResponse>(`/api/search?q=${encodeURIComponent(q)}`),
    enabled: q.length >= 2,
  });

  const results = data?.data;
  const hasAnyResults =
    results &&
    results.customers_total +
      results.suppliers_total +
      results.items_total +
      results.estimates_total +
      results.purchase_orders_total +
      results.sales_returns_total +
      results.purchase_returns_total +
      results.countertop_jobs_total >
      0;
  const tokens = q.split(/\s+/).filter((t) => t.length > 0);

  if (q.length < 2) {
    return (
      <div className="p-6">
        <p className="text-sm text-muted-foreground">Enter at least 2 characters to search.</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="p-6 flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        Searching…
      </div>
    );
  }

  return (
    <div className="p-6 max-w-2xl mx-auto space-y-6">
      <h1 className="text-xl font-bold tracking-tight">Results for &ldquo;{q}&rdquo;</h1>

      {!hasAnyResults && results ? (
        <p className="text-sm text-muted-foreground">No results for &ldquo;{q}&rdquo;.</p>
      ) : (
        <>
          <Section
            title="Customers"
            shown={results?.customers.length ?? 0}
            total={results?.customers_total ?? 0}
            emptyLabel="No customers found"
          >
            {results?.customers.map((c) => (
              <Link
                key={c.id}
                to={`/customers/${c.id}`}
                className="flex items-center justify-between px-4 py-2.5 hover:bg-muted/50 transition-colors"
              >
                <span className="text-sm font-medium">{highlight(c.name, tokens)}</span>
                {c.code && (
                  <span className="text-xs text-muted-foreground">{highlight(c.code, tokens)}</span>
                )}
              </Link>
            ))}
          </Section>

          <Section
            title="Suppliers"
            shown={results?.suppliers.length ?? 0}
            total={results?.suppliers_total ?? 0}
            emptyLabel="No suppliers found"
          >
            {results?.suppliers.map((s) => (
              <Link
                key={s.id}
                to={`/suppliers/${s.id}`}
                className="flex items-center justify-between px-4 py-2.5 hover:bg-muted/50 transition-colors"
              >
                <span className="text-sm font-medium">{highlight(s.name, tokens)}</span>
                {s.code && (
                  <span className="text-xs text-muted-foreground">{highlight(s.code, tokens)}</span>
                )}
              </Link>
            ))}
          </Section>

          <Section
            title="Items"
            shown={results?.items.length ?? 0}
            total={results?.items_total ?? 0}
            emptyLabel="No items found"
          >
            {results?.items.map((item) => (
              <Link
                key={item.id}
                to={`/items/${item.id}`}
                className="flex items-center justify-between px-4 py-2.5 hover:bg-muted/50 transition-colors"
              >
                <span className="flex items-center gap-2">
                  <span className="text-sm font-medium">{highlight(item.name, tokens)}</span>
                  <CategoryBadge category={item.category} />
                </span>
                {item.code && (
                  <span className="text-xs text-muted-foreground">
                    {highlight(item.code, tokens)}
                  </span>
                )}
              </Link>
            ))}
          </Section>

          <Section
            title="Estimates"
            shown={results?.estimates.length ?? 0}
            total={results?.estimates_total ?? 0}
            emptyLabel="No estimates found"
          >
            <TransactionRows
              rows={results?.estimates ?? []}
              hrefFor={(id) => `/estimates/${id}`}
              badgeMap={ESTIMATE_STATUS_BADGE as Record<string, BadgeInfo>}
              tokens={tokens}
            />
          </Section>

          <Section
            title="Purchase Orders"
            shown={results?.purchase_orders.length ?? 0}
            total={results?.purchase_orders_total ?? 0}
            emptyLabel="No purchase orders found"
          >
            <TransactionRows
              rows={results?.purchase_orders ?? []}
              hrefFor={(id) => `/purchase-orders/${id}`}
              badgeMap={PO_STATUS_BADGE as Record<string, BadgeInfo>}
              tokens={tokens}
            />
          </Section>

          <Section
            title="Sales Returns"
            shown={results?.sales_returns.length ?? 0}
            total={results?.sales_returns_total ?? 0}
            emptyLabel="No sales returns found"
          >
            <TransactionRows
              rows={results?.sales_returns ?? []}
              hrefFor={(id) => `/returns/${id}`}
              badgeMap={RETURN_STATUS_BADGE as Record<string, BadgeInfo>}
              tokens={tokens}
            />
          </Section>

          <Section
            title="Purchase Returns"
            shown={results?.purchase_returns.length ?? 0}
            total={results?.purchase_returns_total ?? 0}
            emptyLabel="No purchase returns found"
          >
            <TransactionRows
              rows={results?.purchase_returns ?? []}
              hrefFor={(id) => `/purchase-returns/${id}`}
              badgeMap={RETURN_STATUS_BADGE as Record<string, BadgeInfo>}
              tokens={tokens}
            />
          </Section>

          <Section
            title="Countertop Jobs"
            shown={results?.countertop_jobs.length ?? 0}
            total={results?.countertop_jobs_total ?? 0}
            emptyLabel="No countertop jobs found"
          >
            <TransactionRows
              rows={results?.countertop_jobs ?? []}
              hrefFor={(id) => `/countertop-jobs/${id}`}
              badgeMap={COUNTERTOP_JOB_STATUS_BADGE as Record<string, BadgeInfo>}
              tokens={tokens}
            />
          </Section>
        </>
      )}
    </div>
  );
}
